import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmDialog } from '../../context/ConfirmContext';
import styles from '../../styles/modules/admin/AdminJobOffers.module.css';
import adminCommonStyles from '../../styles/modules/admin/AdminCommon.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface JobOffer {
  _id: string;
  jobRole: string;
  description: string;
  userId: {
    _id: string;
    username: string;
    fullName: string;
    email: string;
    userType: string;
  };
  place: string;
  payRate: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  status: string;
  createdAt: string;
  facilityName?: string;
  payForTransport: boolean;
  numberOfStaff?: number;
}

const AdminJobOffers: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { confirmDelete } = useConfirmDialog();
  const [jobOffers, setJobOffers] = useState<JobOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOffer, setSelectedOffer] = useState<JobOffer | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchJobOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, searchTerm]);

  const fetchJobOffers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });

      const response = await fetch(
        getApiUrl(`api/admin/job-offers?${params}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setJobOffers(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching job offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (offerId: string, currentStatus: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const newStatus = currentStatus === 'open' ? 'closed' : 'open';
      
      const response = await fetch(
        getApiUrl(`api/admin/job-offers/${offerId}/status`),
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (response.ok) {
        showToast({ message: `Job offer ${newStatus === 'open' ? 'opened' : 'closed'} successfully`, type: 'success' });
        fetchJobOffers();
      } else {
        const errorData = await response.json();
        showToast({ message: `Failed to update status: ${errorData.message || 'Unknown error'}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error updating job offer status:', error);
      showToast({ message: 'An error occurred while updating status', type: 'error' });
    }
  };

  const handleDeleteOffer = async (offerId: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete job offer?',
      message: 'Are you sure you want to delete this job offer?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel'
    });
    if (!confirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        getApiUrl(`api/admin/job-offers/${offerId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        showToast({ message: t('admin.jobOffers.deleteSuccess'), type: 'success' });
        fetchJobOffers();
      } else {
        const errorData = await response.json();
        showToast({ message: `${t('admin.jobOffers.deleteFailed')}: ${errorData.message || t('messages.errorOccurred')}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting job offer:', error);
      showToast({ message: t('admin.jobOffers.deleteFailed'), type: 'error' });
    }
  };

  const viewOfferDetails = (offer: JobOffer) => {
    setSelectedOffer(offer);
    setShowModal(true);
  };

  const getStatusBadgeClass = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    const statusMap: Record<string, string> = {
      open: adminCommonStyles.statusOpen,
      closed: adminCommonStyles.statusClosed,
      completed: adminCommonStyles.statusClosed,
      cancelled: adminCommonStyles.statusCancelled,
      expired: adminCommonStyles.statusExpired,
    };

    return [adminCommonStyles.statusBadge, statusMap[normalizedStatus] || adminCommonStyles.statusInactive]
      .filter(Boolean)
      .join(' ');
  };

  if (loading && jobOffers.length === 0) {
    return (
      <div className={styles.adminJobOffers}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.adminJobOffers}>
      <div className={styles.listPageHeader}>
        <button
          className={headerStyles.backButton}
          onClick={() => navigate('/admin')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={styles.headerTitle}>{t('admin.jobOffers.title')}</h1>
        <div></div>
      </div>

      <div className={styles.pageContent}>
        <div className={styles.controlsSection}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder={t('admin.jobOffers.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filters}>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.filterSelect}
            >
              <option value="all">{t('admin.jobOffers.allStatuses')}</option>
              <option value="active">{t('admin.jobOffers.statusActive')}</option>
              <option value="inactive">{t('admin.jobOffers.statusInactive')}</option>
              <option value="expired">{t('admin.jobOffers.statusExpired')}</option>
            </select>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.offersTable}>
            <thead>
              <tr>
                <th>{t('admin.jobOffers.jobRole')}</th>
                <th>{t('admin.jobOffers.employer')}</th>
                <th>{t('admin.jobOffers.location')}</th>
                <th>{t('admin.jobOffers.date')}</th>
                <th>{t('admin.jobOffers.time')}</th>
                <th>{t('admin.jobOffers.payRate')}</th>
                <th>{t('admin.jobOffers.status')}</th>
                <th>{t('admin.jobOffers.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {jobOffers.map((offer) => (
                <tr key={offer._id}>
                  <td>
                    <strong>{offer.jobRole}</strong>
                  </td>
                  <td>{offer.facilityName || offer.userId.fullName}</td>
                  <td>{offer.place}</td>
                  <td>
                    {offer.date 
                      ? new Date(offer.date).toLocaleDateString(t('common.locale') || 'en-US')
                      : offer.startDate && offer.endDate
                      ? `${new Date(offer.startDate).toLocaleDateString(t('common.locale') || 'en-US')} - ${new Date(offer.endDate).toLocaleDateString(t('common.locale') || 'en-US')}`
                      : 'N/A'
                    }
                  </td>
                  <td>
                    {offer.startTime 
                      ? offer.endTime 
                        ? `${offer.startTime} - ${offer.endTime}`
                        : offer.startTime
                      : 'N/A'
                    }
                  </td>
                  <td>{offer.payRate}</td>
                  <td>
                    <span className={getStatusBadgeClass(offer.status)}>
                      {offer.status.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        className={adminCommonStyles.btnView}
                        onClick={() => viewOfferDetails(offer)}
                        title={t('admin.jobOffers.viewDetails')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                      <button
                        className={`${adminCommonStyles.btnToggle} ${offer.status === 'open' ? adminCommonStyles.btnSuspend : adminCommonStyles.btnActivate}`}
                        onClick={() => handleStatusToggle(offer._id, offer.status)}
                        title={offer.status === 'open' ? 'Close' : 'Open'}
                      >
                        {offer.status === 'open' ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                        )}
                      </button>
                      <button
                        className={adminCommonStyles.btnDelete}
                        onClick={() => handleDeleteOffer(offer._id)}
                        title={t('admin.jobOffers.delete')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {jobOffers.length === 0 && (
          <div className={styles.noResults}>
            {t('admin.jobOffers.noResults')}
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← {t('common.previous')}
            </button>
            <span className={styles.paginationInfo}>
              {t('common.pageOf', { current: currentPage, total: totalPages })}
            </span>
            <button
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.next')} →
            </button>
          </div>
        )}
      </div>

      {showModal && selectedOffer && (
        <div className={adminCommonStyles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={adminCommonStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>{selectedOffer.jobRole}</h2>
              <button className={adminCommonStyles.modalClose} onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>
            <div className={adminCommonStyles.modalBody}>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Job Role:</span>
                <span className={adminCommonStyles.detailValue}>{selectedOffer.jobRole}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.jobOffers.employer')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedOffer.userId.fullName} ({selectedOffer.userId.email})</span>
              </div>
              {selectedOffer.facilityName && (
                <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                  <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Facility:</span>
                  <span className={adminCommonStyles.detailValue}>{selectedOffer.facilityName}</span>
                </div>
              )}
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.jobOffers.location')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedOffer.place}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.jobOffers.payRate')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedOffer.payRate}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Transport:</span>
                <span className={adminCommonStyles.detailValue}>{selectedOffer.payForTransport ? 'Paid' : 'Not paid'}</span>
              </div>
              {selectedOffer.numberOfStaff && (
                <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                  <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Number of Staff:</span>
                  <span className={adminCommonStyles.detailValue}>{selectedOffer.numberOfStaff}</span>
                </div>
              )}
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Date & Time:</span>
                <span className={adminCommonStyles.detailValue}>
                  {selectedOffer.date 
                    ? `${new Date(selectedOffer.date).toLocaleDateString(t('common.locale') || 'en-US')}${selectedOffer.startTime ? ` (${selectedOffer.startTime}${selectedOffer.endTime ? ` - ${selectedOffer.endTime}` : ''})` : ''}`
                    : selectedOffer.startDate && selectedOffer.endDate
                    ? `${new Date(selectedOffer.startDate).toLocaleDateString(t('common.locale') || 'en-US')} - ${new Date(selectedOffer.endDate).toLocaleDateString(t('common.locale') || 'en-US')}`
                    : 'N/A'
                  }
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.jobOffers.status')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  <span className={getStatusBadgeClass(selectedOffer.status)}>
                    {selectedOffer.status.toUpperCase()}
                  </span>
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.jobOffers.created')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  {new Date(selectedOffer.createdAt).toLocaleString()}
                </span>
              </div>
              {selectedOffer.description && (
                <div className={styles.detailFull}>
                  <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.jobOffers.description')}:</span>
                  <p className={styles.detailDescription}>{selectedOffer.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobOffers;
